import React from 'react';
import { FieldArray } from 'redux-form';
import { Row, Col, FormGroup, Button } from 'reactstrap';
import { Utils } from '../../services';
import FormLayout from '../../models/FormLayout';
import { formGroupReduxPrefix, getSortedFieldsInGroup } from '../../services/formLayoutGroupUtils';

function FieldRequiredError({ show, message }) {
    if (!show) {
        return null;
    }
    return (
        <div className="invalid-feedback d-block form-wizard-field-error-msg">
            {message}
        </div>
    );
}

/**
 * @param {object} props
 * @param {import('../../models/FormLayoutFieldGroup').default} props.group
 * @param {import('../../models/FormLayoutField').default[]} props.allFields
 * @param {{ lang: string, resources?: object }} props.context
 * @param {Set<string>} [props.invalidPaths]
 * @param {string} [props.fieldRequiredMessage]
 */
export function FormRendererSingleGroup({
    group,
    allFields,
    context,
    invalidPaths,
    fieldRequiredMessage,
}) {
    const prefix = formGroupReduxPrefix(group.id);
    const members = getSortedFieldsInGroup(allFields, group.id);
    const paths = invalidPaths || new Set();

    return (
        <div className="form-layout-group form-layout-group--single border rounded p-3 mb-3">
            <div className="font-weight-bold mb-2 text-left">
                {Utils.localized_or_fallback(group.label, context.lang, group.name)}
            </div>
            <Row>
                {members.map((field) => {
                    const fieldPath = `${prefix}.${field.id}`;
                    const hasError = paths.has(fieldPath);
                    return (
                        <Col
                            key={field.id}
                            sm={12}
                            md={Math.round(field.width * 12)}
                            data-wizard-error={hasError ? 'true' : undefined}
                        >
                            <FormGroup row className={hasError ? 'form-wizard-field--invalid' : undefined}>
                                {field.getLabel(context) && (
                                    <Col sm={12} className="text-left font-weight-bold">
                                        {field.getLabel(context)}
                                    </Col>
                                )}
                                {field.getDescription(context) && (
                                    <Col sm={12} className="text-left">
                                        {field.getDescription(context)}
                                    </Col>
                                )}
                                <Col sm={12} className={hasError ? 'form-wizard-field-input--invalid' : undefined}>
                                    {FormLayout.isDisplayOnlyField(field.type)
                                        ? field.getInput(context)
                                        : field.getInput({ ...context, reduxName: fieldPath })}
                                    <FieldRequiredError show={hasError} message={fieldRequiredMessage} />
                                </Col>
                            </FormGroup>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
}

/**
 * Must stay a stable function reference — passing an inline lambda to FieldArray's
 * `component` remounts the array on every parent render and drops input focus.
 *
 * @param {object} props
 * @param {import('redux-form').WrappedFieldArrayProps} props.fields
 * @param {import('redux-form').WrappedFieldArrayProps['meta']} props.meta
 */
function FormRendererRepeatableGroupRows({
    fields,
    meta: _meta,
    group,
    allFields,
    context,
    t,
    invalidPaths,
    fieldRequiredMessage,
}) {
    const minRows = Math.max(1, Number(group.minItems ?? group.min_items) || 1);
    const maxRows = Math.max(minRows, Number(group.maxItems ?? group.max_items) || 50);
    const members = getSortedFieldsInGroup(allFields, group.id);
    const paths = invalidPaths || new Set();
    const prefix = formGroupReduxPrefix(group.id);

    return (
        <div className="form-layout-group form-layout-group--multiple border rounded p-3 mb-3">
            <div className="font-weight-bold mb-2 text-left">
                {Utils.localized_or_fallback(group.label, context.lang, group.name)}
            </div>
            {fields.map((member, index) => (
                <div key={member} className="border rounded p-2 mb-2 bg-light">
                    <Row>
                        {members.map((field) => {
                            const fieldPath = `${prefix}[${index}].${field.id}`;
                            const hasError = paths.has(fieldPath);
                            return (
                                <Col
                                    key={field.id}
                                    sm={12}
                                    md={Math.round(field.width * 12)}
                                    data-wizard-error={hasError ? 'true' : undefined}
                                >
                                    <FormGroup row className={hasError ? 'form-wizard-field--invalid' : undefined}>
                                        {field.getLabel(context) && (
                                            <Col sm={12} className="text-left font-weight-bold">
                                                {field.getLabel(context)}
                                            </Col>
                                        )}
                                        {field.getDescription(context) && (
                                            <Col sm={12} className="text-left">
                                                {field.getDescription(context)}
                                            </Col>
                                        )}
                                        <Col sm={12} className={hasError ? 'form-wizard-field-input--invalid' : undefined}>
                                            {FormLayout.isDisplayOnlyField(field.type)
                                                ? field.getInput(context)
                                                : field.getInput({
                                                    ...context,
                                                    reduxName: `${member}.${field.id}`,
                                                })}
                                            <FieldRequiredError show={hasError} message={fieldRequiredMessage} />
                                        </Col>
                                    </FormGroup>
                                </Col>
                            );
                        })}
                    </Row>
                    {fields.length > minRows && (
                        <Button
                            type="button"
                            size="sm"
                            color="dark"
                            className="mt-1"
                            onClick={() => fields.remove(index)}
                        >
                            <i className="fa fa-trash" aria-hidden="true" />
                            &nbsp;
                            {Utils.localized_or_fallback(group.removeRowLabel, context.lang) ||
                                t('forms.group_remove_row')}
                        </Button>
                    )}
                </div>
            ))}
            {fields.length < maxRows && (
                <Button
                    type="button"
                    size="sm"
                    color="info"
                    className="mt-1"
                    onClick={() => fields.push({})}
                >
                    <i className="fa fa-plus" aria-hidden="true" />
                    &nbsp;
                    {Utils.localized_or_fallback(group.addRowLabel, context.lang) ||
                        t('forms.group_add_row')}
                </Button>
            )}
        </div>
    );
}

/**
 * @param {object} props
 * @param {import('../../models/FormLayoutFieldGroup').default} props.group
 * @param {import('../../models/FormLayoutField').default[]} props.allFields
 * @param {{ lang: string, resources?: object }} props.context
 * @param {Set<string>} [props.invalidPaths]
 * @param {string} [props.fieldRequiredMessage]
 */
export function FormRendererRepeatableGroup({
    group,
    allFields,
    context,
    t,
    invalidPaths,
    fieldRequiredMessage,
}) {
    const prefix = formGroupReduxPrefix(group.id);
    return (
        <FieldArray
            name={prefix}
            component={FormRendererRepeatableGroupRows}
            group={group}
            allFields={allFields}
            context={context}
            t={t}
            invalidPaths={invalidPaths}
            fieldRequiredMessage={fieldRequiredMessage}
        />
    );
}
